import React, { memo, useState, useCallback, useMemo, useEffect } from "react";
import { v4 as uuidv4 } from 'uuid';

import { useSelector } from "react-redux";

import Select from "react-select";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";

import { useTranslation } from "react-i18next";

import {
  ButtonDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";

import "./style.scss";

function InputComponent({
  label,
  name,
  type,
  value,
  onChange,
  placeholder,
  error,
  required,
  children,
  innerRef,
  defaultValue,
  buttonReset,
  ...rest
}) {
  const { t } = useTranslation();
  const { mainTheme } = useSelector((state) => state.Layout);
  const [myId, setMyId] = useState(uuidv4());
  
  const resetInput = useCallback(()=>{
    onChange?.({
      target: {
        value: '',
      },
    });
  },[onChange]); 

  useEffect(() => {
    if (defaultValue) {
      onChange?.({
        target: {
          value: defaultValue,
        },
      });
    }
  }, [defaultValue, onChange]);

  useEffect(()=>{
    
    const target = document.getElementById(`${myId}`);

    if(buttonReset && value){     
      target?.children?.[1]?.classList?.add('active-input-reset');          
    }else{
      target?.children?.[1]?.classList?.remove('active-input-reset');
    }
  },[buttonReset, value, myId]);

  

  return (
    <div id={`${myId}`} className={`input-component ${mainTheme}`}>
      <label htmlFor={name} className="label">
        {label}
        {required && <span className="required">*</span>}
      </label>
      {children ? (
        children
      ) : (
        <input
          type={type}
          name={name}
          className={`${error ? "error" : ""} ${buttonReset && value ? 'active-input-reset' : ''}`}
          id={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          ref={innerRef}
          defaultValue={defaultValue}
          {...rest}
        />
      )}
      {buttonReset && value && <span onClick={resetInput} className="button-reset">X</span>}
      {error && <span className="error">{t(error)}</span>}
    </div>
  );
}

export default memo(InputComponent);

export const SelectComponent = memo(
  ({
    label,
    name,
    value,
    onChange,
    placeholder,
    error,
    required,
    register,
    options,
    defaultValue,
    ...rest
  }) => {
    const { mainTheme } = useSelector((state) => state.Layout);

    const { t } = useTranslation();

    const customStyles = useMemo(
      () => ({
        control: (provided) => ({
          ...provided,
          backgroundColor: mainTheme === "dark" ? "#2a3042" : "#fff",
          border: error ? "1px solid #F46A6A" : "none",
          width: "100%",
          color: mainTheme === "dark" ? "#fff" : "#000",
          borderColor: "transparent",
          boxShadow: "none",
          "&:hover": {
            borderColor: "transparent",
          },
          "&:focus": {
            borderColor: "transparent",
          },
          "&:focus-within": {
            borderColor: "transparent",
          },
        }),
        option: (provided, state) => ({
          ...provided,
          backgroundColor: state.isFocused ? "#556ee6" : "#2a3042",
          color: mainTheme === "dark" ? "#fff" : "#000",
          textTransform: "capitalize",
          "&:hover": {
            backgroundColor: "#556ee6",
          },
        }),
        menu: (provided) => ({
          ...provided,
          backgroundColor: mainTheme === "dark" ? "#2a3042" : "#fff",
        }),
        singleValue: (provided) => ({
          ...provided,
          color: mainTheme === "dark" ? "#fff" : "#000",
          textTransform: "capitalize",
        }),
        input: (provided) => ({
          ...provided,
          color: mainTheme === "dark" ? "#fff" : "#000",
        }),
        multiValue: (base, state) => ({
          ...base,
          backgroundColor: state.data.isFixed ? "gray" : base.backgroundColor,
          textTransform: "capitalize",
        }),
        multiValueLabel: (base, state) => {
          return state.data.isFixed
            ? { ...base, fontWeight: "bold", color: "white", paddingRight: 6 }
            : base;
        },
        multiValueRemove: (base, state) => ({
          ...base,
          display: state.data.isFixed ? "none" : "flex",
          color: "#F46A6A",
        }),
        menuList: (base) => ({
          ...base,
          "::-webkit-scrollbar": {
            width: "0.5rem",
            height: "0.5rem",
          },
          "::-webkit-scrollbar-track": {
            borderRadius: "0.5rem",
          },
          "::-webkit-scrollbar-thumb": {
            background: "radial-gradient(circle, #8d8e90 100%, #f9d4ff 100%)",
            borderRadius: "10px",
          },
        }),
        placeholder: (base) => ({
          ...base,
          color: mainTheme === "dark" ? "#fff" : "#000",
          opacity: 0.5,
        }),
        dropdownIndicator: (base) => ({
          ...base,
          color: mainTheme === "dark" ? "#fff" : "#000",
          opacity: 0.5,
          "&:hover": {
            color: mainTheme === "dark" ? "#fff" : "#000",
            opacity: 1,
          },
        }),
        indicatorSeparator: (base) => ({
          ...base,
          display: "none",
        }),
        clearIndicator: (base) => ({
          ...base,
          color: mainTheme === "dark" ? "#fff" : "#000",
          opacity: 0.5,
          "&:hover": {
            color: mainTheme === "dark" ? "#fff" : "#000",
            opacity: 1,
          },
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }),
      [mainTheme, error]
    );

    return (
      <>
        <Select
          styles={customStyles}
          className={`input-component ${error ? "error" : ""}`}
          placeholder={placeholder}
          options={options}
          value={value}
          onChange={onChange}
          noOptionsMessage={() => t("No Options")}
          ref={register}
          defaultValue={defaultValue}
          {...rest}
        />
        {error && <span className="error">{t(error)}</span>}
      </>
    );
  }
);

const ItemDropdown = memo(({ option, optionsSelected, handleSelect }) => {
  const { t } = useTranslation();

  return (
    <DropdownItem
      key={option.value}
      onClick={() => handleSelect(option)}
      className="dropdown-item"
      toggle={false}
    >
      <div className="d-flex align-items-center justify-content-start custom-checkbox ml-3">
        <input
          id={option.value}
          type="checkbox"
          className="custom-control-input pointer"
          checked={optionsSelected.some((item) => item.value === option.value)}
          onChange={() => handleSelect(option)}
        />
        <label className="custom-control-label" htmlFor={option.value}>
          {t(option.label)}
        </label>
      </div>
    </DropdownItem>
  );
});

export const ExtraInputComponent = memo(
  ({
    name,
    options,
    type,
    onChangeInput,
    onChangeSelect,
    placeholder,
    error,
    required,
    register,
    defaultValue,
    defaultValueTypes,
    valueInput,
    typeInputRadio = false,
    buttonReset = false,
    labelDropdown,
    reverteBordeButtonDrop = false,
    ...rest
  }) => {
    const { t } = useTranslation();

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [optionsSelected, setOptionsSelected] = useState([]);

    const toggle = () => setDropdownOpen((prevState) => !prevState);

    const handleSelect = useCallback((option) => {
      if(typeInputRadio){
        const newsOptions = []
        newsOptions.push(option);
        setOptionsSelected(newsOptions);
      }else{
        setOptionsSelected((prevState) => {
          const newOptions = [...prevState];
          const index = newOptions.findIndex(
            (item) => item.value === option.value
          );
          if (index === -1) {
            newOptions.push(option);
          } else {
            newOptions.splice(index, 1);
          }

          return newOptions;
        });
      }
    }, [typeInputRadio]);

    useEffect(() => {
      onChangeSelect(optionsSelected.map((item) => item.value));
    }, [optionsSelected, onChangeSelect]);

    useEffect(() => {
      if (defaultValueTypes) {
        setOptionsSelected(
          options?.filter((item) => defaultValueTypes?.includes(item.value)) ||
            options
        );
      }
    }, [defaultValueTypes, options]);

    useEffect(() => {
      if (defaultValue) {
        onChangeInput?.(defaultValue);
      }
    }, [defaultValue, onChangeInput]);

    return (
      <div className={`input-component`}>
        <div
          className="d-flex flex-col align-items-end w-full extra-content"
          ref={register}
        >
          
          <ButtonDropdown
            isOpen={dropdownOpen}
            color="primary"
            toggle={toggle}
            className={`button-dropdown`}
          >
            {
              labelDropdown && (
                <label className="label label-drop">
                  {labelDropdown}
                </label>
              )
            }
            <DropdownToggle
              caret
              className={`bx bx-filter-alt search-icon icon-button ${
                error?.types ? "error" : ""
              } ${reverteBordeButtonDrop ? 'button-drop-radius' : ''}`}
              color="primary"
            />
            <DropdownMenu ref={() => register(`${name}.types`)}>
              {options.map((option) => (
                <ItemDropdown
                  key={option.value}
                  option={option}
                  optionsSelected={optionsSelected}
                  handleSelect={handleSelect}
                />
              ))}
            </DropdownMenu>
          </ButtonDropdown>
          <InputComponent
            name={`${name}.value`}
            type={type}
            onChange={onChangeInput}
            placeholder={placeholder}
            className={error?.value ? "error" : ""}
            required={required}
            innerRef={register}
            defaultValue={defaultValue}
            autoComplete="off"
            buttonReset={buttonReset}
            value={rest?.value ?? valueInput}
            {...rest}
          />
        </div>
        {error?.types && (
          <span className="error">{t(error.types?.message)}</span>
        )}
        {error?.value && (
          <span className="error">{t(error.value?.message)}</span>
        )}
      </div>
    );
  }
);

export const DatePickerComponent = memo(
  ({
    name,
    defaultValue,
    onChange,
    placeholder,
    error,
    required,
    register,
    ...rest
  }) => {
    const { t } = useTranslation();
    const { mainTheme } = useSelector((state) => state.Layout);
    const [range, setRange] = useState(
      defaultValue ?? [new Date(), new Date()]
    );

    const handleSelect = useCallback(
      (dates) => {
        setRange(dates);
        onChange?.(dates);
      },
      [onChange]
    );

    useEffect(() => {
      handleSelect(range);
    }, [range, handleSelect]);

    useEffect(() => {
      if (defaultValue) {
        setRange(defaultValue);
      } else {
        setRange([new Date(), new Date()]);
      }
    }, [defaultValue]);

    return (
      <div className={`input-component ${error ? "error" : ""} `}>
        <DateRangePicker
          onChange={handleSelect}
          value={range}
          calendarClassName={`rdrCalendar ${mainTheme}`}
          className={`input-component calendar ${error ? "error" : ""}`}
          calendarIcon={<i className="bx bx-calendar"></i>}
          clearIcon={null}
          maxDate={new Date()}
          name={name}
          format="dd/MM/yyyy"
          locale="pt-BR"
          rangeDivider=" <-> "
          showLeadingZeros
          showSelectionPreview
          ref={register}
          {...rest}
        />
        {error?.message && <span className="error">{t(error?.message)}</span>}
      </div>
    );
  }
);
